import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

// Teletorget-style Refund Modal (Canvas Preview)
// - Tailwind-only, self-contained
// - Provider badge (Vipps/Stripe)
// - Status chip
// - Safe confirm (type REFUND)

type Provider = "vipps" | "stripe";
type PaymentStatus = "authorized" | "captured" | "paid" | "refunded" | "failed";
type RefundReason =
    | "Duplicate"
    | "Fraudulent"
    | "Requested by customer"
    | "Other"
    | "Mark for internal review";

const cn = (...classes: Array<string | false | undefined>) =>
    classes.filter(Boolean).join(" ");

function clampNumber(n: number, min: number, max: number) {
    if (Number.isNaN(n)) return min;
    return Math.max(min, Math.min(max, n));
}

function ProviderBadge({ provider }: { provider: Provider }) {
    const label = provider === "vipps" ? "Vipps" : "Stripe";
    return (
        <span
            className={cn(
                "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold",
                provider === "vipps"
                    ? "border-orange-200 bg-orange-50 text-orange-700"
                    : "border-indigo-200 bg-indigo-50 text-indigo-700"
            )}
        >
            <span
                className={cn(
                    "h-2 w-2 rounded-full",
                    provider === "vipps" ? "bg-orange-500" : "bg-indigo-600"
                )}
            />
            {label}
        </span>
    );
}

function StatusChip({ status }: { status: PaymentStatus }) {
    const map: Record<PaymentStatus, { label: string; cls: string }> = {
        authorized: {
            label: "Authorized",
            cls: "border-slate-200 bg-slate-50 text-slate-700",
        },
        captured: {
            label: "Captured",
            cls: "border-emerald-200 bg-emerald-50 text-emerald-700",
        },
        paid: {
            label: "Paid",
            cls: "border-emerald-200 bg-emerald-50 text-emerald-700",
        },
        refunded: {
            label: "Refunded",
            cls: "border-amber-200 bg-amber-50 text-amber-700",
        },
        failed: {
            label: "Failed",
            cls: "border-rose-200 bg-rose-50 text-rose-700",
        },
    };
    const m = map[status];
    return (
        <span
            className={cn(
                "inline-flex rounded-full border px-3 py-1 text-xs font-semibold",
                m.cls
            )}
        >
            {m.label}
        </span>
    );
}

function KeyValue({ k, v }: { k: string; v?: string }) {
    return (
        <div className="flex items-center justify-between gap-3">
            <span className="text-slate-500">{k}</span>
            <span className="text-right font-semibold text-slate-900">{v || "—"}</span>
        </div>
    );
}

interface ReleaseModalProps {
    openReleaseModal: boolean;
    onClose: () => void;
    currentUser: any,
    selectedCall: any,
    client: any,
    baseUrl: string,
}
export default function ReleaseModal({ openReleaseModal, onClose, currentUser, selectedCall, client, baseUrl }: ReleaseModalProps) {
    // Demo data (replace with real data)
    const [provider, setProvider] = useState<Provider>(selectedCall?.payment_intent_id ? "stripe" : "vipps");
    const [status, setStatus] = useState<PaymentStatus>("paid");

    const currency: "NOK" = client.price;
    const maxRefundableNok = selectedCall?.charge_price;

    const customerName = selectedCall?.customer?.name;
    const customerPhone = selectedCall?.customer?.country + " " + selectedCall?.customer?.phone;
    const callId = selectedCall?.cid;
    const paymentId = provider === "vipps" ? selectedCall?.reference : selectedCall?.payment_intent_id;
    const createdAtLabel = selectedCall?.created_at;

    const [amount, setAmount] = useState<number>(maxRefundableNok);
    const [reason, setReason] = useState<RefundReason>("Duplicate");
    const [note, setNote] = useState("");
    const [notifyCustomer, setNotifyCustomer] = useState(true);
    const [confirmText, setConfirmText] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");

    const requiresConfirm = amount > 0;
    const confirmOk = !requiresConfirm || confirmText.trim().toUpperCase() === "REFUND";

    const canRefund = useMemo(() => {
        const validAmount = amount > 0 && amount <= maxRefundableNok;
        const statusOk = status !== "refunded" && status !== "failed";
        return validAmount && statusOk && confirmOk && !loading;
    }, [amount, maxRefundableNok, status, confirmOk, loading]);

    async function handleRefund() {
        setError("");
        try {
            setLoading(true);
            let formData = new FormData();
            formData.append("token", currentUser.token);
            formData.append("amount", amount.toString());
            formData.append("reason", reason);
            formData.append("note", note);
            formData.append("max_amount", maxRefundableNok);
            formData.append("type", "release");
            formData.append("call_id", callId);
            formData.append("client_uid", client?.uid);

            const response = await axios.post(baseUrl + '/supportCreateRefund', formData);
            if(response.data.status === "OK"){
                setStatus("refunded");
            }
        } catch (e: any) {
            setError(e?.message || "Something went wrong while creating the refund.");
        } finally {
            setLoading(false);
        }
    }

    return (
        <AnimatePresence>
            {openReleaseModal && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    />

                    {/* Centered modal wrapper */}
                    <motion.div
                        className="fixed inset-0 z-50 flex items-center justify-center p-4"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                    >

                        <div className="w-full max-w-3xl rounded-2xl border border-slate-200 bg-white shadow-2xl max-h-[85vh] overflow-y-auto">
                            {/* Header */}
                            <div className="shrink-0 flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
                                <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <h2 className="text-lg font-semibold text-slate-900">Refund payment</h2>
                                        <ProviderBadge provider={provider} />
                                        <StatusChip status={status} />
                                    </div>
                                    <p className="mt-1 text-sm text-slate-500">
                                        {`Call: ${createdAtLabel} • ID: ${callId}`}
                                    </p>
                                </div>

                                <button
                                    className="rounded-xl p-2 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                                    onClick={onClose}
                                    aria-label="Close"
                                    title="Close"
                                >
                                    ✕
                                </button>
                            </div>

                            {/* Body */}
                            <div className="flex-1 px-6 py-6">
                                <div className="grid gap-6 px-6 py-6 md:grid-cols-[1.2fr_0.8fr]">
                                    {/* Left */}
                                    <div className="space-y-5">
                                        {error ? (
                                            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                                                {error}
                                            </div>
                                        ) : null}

                                        <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4">
                                            <label className="text-sm font-medium text-slate-700">Refund amount</label>
                                            <div className="mt-2 flex items-center gap-2">
                                                <div className="flex w-full items-center rounded-xl border border-slate-200 bg-white px-3 py-2 focus-within:ring-2 focus-within:ring-slate-900/10">
                                                    <input
                                                        type="number"
                                                        inputMode="numeric"
                                                        min={0}
                                                        max={maxRefundableNok}
                                                        value={amount}
                                                        onChange={(e) =>
                                                            setAmount(
                                                                clampNumber(Number(e.target.value), 0, maxRefundableNok)
                                                            )
                                                        }
                                                        className="w-full bg-transparent text-base font-semibold text-slate-900 outline-none"
                                                    />
                                                    <span className="ml-2 text-xs font-semibold text-slate-500">
                                                        {currency}
                                                    </span>
                                                </div>

                                                <button
                                                    type="button"
                                                    className="whitespace-nowrap rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                                                    onClick={() => setAmount(maxRefundableNok)}
                                                    disabled={loading}
                                                >
                                                    Max
                                                </button>
                                            </div>
                                            <p className="mt-2 text-xs text-slate-500">
                                                Refundable: <span className="font-semibold">{maxRefundableNok}</span> {currency}
                                            </p>
                                        </div>

                                        <div className="grid gap-4 sm:grid-cols-2">
                                            <div>
                                                <label className="text-sm font-medium text-slate-700">Reason</label>
                                                <select
                                                    value={reason}
                                                    onChange={(e) => setReason(e.target.value as RefundReason)}
                                                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-slate-900/10"
                                                    disabled={loading}
                                                >
                                                    <option value="Duplicate">Duplicate payment</option>
                                                    <option value="Fraudulent">Fraudulent</option>
                                                    <option value="Requested by customer">Requested by customer</option>
                                                    <option value="Mark for internal review">Mark for internal review</option>
                                                    <option value="Other">Other</option>
                                                </select>
                                            </div>

                                            <div className="flex items-end">
                                                <label className="flex w-full items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
                                                    <div>
                                                        <div className="text-sm font-semibold text-slate-900">Notify customer</div>
                                                        <div className="text-xs text-slate-500">Send SMS/email receipt if available</div>
                                                    </div>
                                                    <input
                                                        type="checkbox"
                                                        checked={notifyCustomer}
                                                        onChange={(e) => setNotifyCustomer(e.target.checked)}
                                                        disabled={loading}
                                                        className="h-5 w-5"
                                                    />
                                                </label>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="text-sm font-medium text-slate-700">Internal note</label>
                                            <textarea
                                                value={note}
                                                onChange={(e) => setNote(e.target.value)}
                                                placeholder="Add details for auditing (optional)"
                                                rows={4}
                                                disabled={loading}
                                                className="mt-2 w-full resize-none rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-slate-900/10"
                                            />
                                            <p className="mt-2 text-xs text-slate-500">
                                                Tip: include ticket id, who approved, and what was delivered.
                                            </p>
                                        </div>

                                        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
                                            <div className="text-sm font-semibold text-amber-900">Heads up</div>
                                            <div className="mt-1 text-sm text-amber-800">
                                                Refunds are irreversible. Make sure the call/payment matches this transaction.
                                            </div>
                                        </div>

                                        <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                            <label className="text-sm font-medium text-slate-700">
                                                Confirm (type <span className="font-semibold">REFUND</span>)
                                            </label>
                                            <input
                                                value={confirmText}
                                                onChange={(e) => setConfirmText(e.target.value)}
                                                disabled={loading}
                                                placeholder="REFUND"
                                                className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-slate-900/10"
                                            />
                                            <p className="mt-2 text-xs text-slate-500">
                                                This prevents accidental refunds (especially on touch screens).
                                            </p>
                                        </div>
                                    </div>

                                    {/* Right */}
                                    <div className="space-y-4">
                                        <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                            <div className="text-sm font-semibold text-slate-900">Payment summary</div>

                                            <div className="mt-4 space-y-3 text-sm">
                                                <KeyValue k="Customer" v={customerName} />
                                                <KeyValue k="Phone" v={customerPhone} />
                                                <div className="flex items-center justify-between gap-3">
                                                    <span className="text-slate-500">Call ID</span>
                                                    <span className="font-mono text-xs font-semibold text-slate-900">{callId}</span>
                                                </div>
                                                <div className="flex items-center justify-between gap-3">
                                                    <span className="text-slate-500">Payment ID</span>
                                                    <span className="font-mono text-xs font-semibold text-slate-900">{paymentId}</span>
                                                </div>

                                                <div className="my-2 h-px bg-slate-100" />

                                                <div className="flex items-center justify-between">
                                                    <span className="text-slate-500">Refund now</span>
                                                    <span className="text-base font-extrabold text-slate-900">
                                                        {amount} {currency}
                                                    </span>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-slate-500">Remaining refundable</span>
                                                    <span className="font-semibold text-slate-900">
                                                        {Math.max(0, maxRefundableNok - amount)} {currency}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                            <div className="text-sm font-semibold text-slate-900">Audit trail</div>
                                            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-600">
                                                <li>Stores reason + note</li>
                                                <li>Links refund to call/payment id</li>
                                                <li>Optional customer notification</li>
                                            </ul>
                                        </div>

                                        <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                            <div className="text-sm font-semibold text-slate-900">Quick actions</div>
                                            <div className="mt-3 grid gap-2">
                                                <button className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                                                    View call details
                                                </button>
                                                <button className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                                                    Open payment in provider
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="flex flex-col-reverse gap-3 border-t border-slate-100 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
                                <button
                                    className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                                    onClick={onClose}
                                    disabled={loading}
                                >
                                    Cancel
                                </button>

                                <button
                                    className={cn(
                                        "rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-sm",
                                        canRefund
                                            ? "bg-slate-900 hover:bg-slate-800"
                                            : "bg-slate-300 cursor-not-allowed"
                                    )}
                                    onClick={handleRefund}
                                    disabled={!canRefund}
                                >
                                    {loading ? "Processing…" : `Refund ${currency} ${amount}`}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
